const getHref = (name: string) => `${process.env.NX_PUBLIC_CLOUD_PAYSEND_API}/consumer3/svg/${name}.svg#symb`;

const fetchIcon = async (name: string) =>
    await fetch(getHref(name), {
        method: 'GET',
        headers: new Headers({ accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8' }),
    });

const getIconFromCache = async (name: string, cacheName = 'icon-cache', expirationDays = 30) => {
    const expirationTime = expirationDays * 24 * 60 * 60 * 1000;
    try {
        const cache = await caches.open(`${cacheName}`);
        const cachedResponse = await cache.match(name);

        if (cachedResponse) {
            // Check for expiration
            const cachedText = await cachedResponse.text();
            const storedTime = cachedResponse.headers.get('x-stored-time');
            if (storedTime && Date.now() - Number(storedTime) < expirationTime) {
                return cachedText; // Return cached SVG if valid
            } else {
                // Remove expired entry from cache
                await cache.delete(name);
            }
        }

        // Fetch and cache
        const response = await fetchIcon(name);
        if (!response.ok) {
            throw new Error(`Failed to fetch icon: ${response.status} ${response.statusText}`);
        }
        const text = await response.text();

        // Add expiration header
        const newResponse = new Response(text, {
            headers: {
                'Content-Type': 'image/svg+xml',
                'x-stored-time': Date.now().toString(), // Store current time
            },
        });
        await cache.put(name, newResponse);
        return text;
    } catch (error) {
        console.error('Error fetching and caching icon:', error);
        return null;
    }
};

export const fetchAndCacheIcon = async (
    name: string,
    defaultName = 'ic_placeholder',
    cacheName = 'icon-cache',
    expirationDays = 30,
): Promise<string | null | undefined> => {
    if (!('caches' in window)) {
        console.warn('Cache API is not supported in this browser.');
        try {
            let response = await fetchIcon(name);
            if (!response.ok) {
                response = await fetchIcon(defaultName);
            }
            if (!response.ok) {
                throw new Error(`Failed to fetch icon: ${response.status} ${response.statusText}`);
            }
            return await response.text();
        } catch (error) {
            console.error('Error fetching icon without cache:', error);
            return null;
        }
    }

    const icon = await getIconFromCache(name, cacheName, expirationDays);
    if (!icon) {
        return await getIconFromCache(defaultName, cacheName, expirationDays);
    }
    return icon;
};
